import type { Application } from "pixi.js";

export type GraphicsQuality = "balanced" | "battery";
const KEY = "empire.graphics.v1";
const listeners = new Set<() => void>();
let quality: GraphicsQuality = "balanced";
try {
  if (localStorage.getItem(KEY) === "battery") quality = "battery";
} catch {
  /* Private browsing. */
}
export const graphicsQuality = (): GraphicsQuality => quality;
export function subscribeGraphics(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
export function setGraphicsQuality(next: GraphicsQuality): void {
  quality = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    /* Preference remains valid this session. */
  }
  for (const listener of listeners) listener();
}
export const renderResolution = (): number =>
  Math.min(window.devicePixelRatio || 1, quality === "battery" ? 1.25 : 2);
export const decorativeBudget = (): number => (quality === "battery" ? 0.5 : 1);

/** Explicit quality control, hidden-tab suspension, and bounded frame-time diagnostics. */
export function managePresentation(app: Application): () => void {
  const samples: number[] = [];
  let last = 0;
  const apply = (): void => {
    app.ticker.maxFPS = quality === "battery" ? 30 : 60;
    const host = app.canvas.parentElement;
    // Use layout dimensions, not a fractional canvas size rounded by the
    // previous density. Repeated quality changes must not grow the canvas.
    app.renderer.resize(
      host?.clientWidth ?? app.screen.width,
      host?.clientHeight ?? app.screen.height,
      renderResolution()
    );
  };
  const visibility = (): void => {
    last = 0;
    if (document.hidden) app.stop();
    else app.start();
  };
  const measure = (): void => {
    const now = performance.now();
    if (last && now - last < 250) {
      samples.push(now - last);
      if (samples.length > 300) samples.shift();
    }
    last = now;
  };
  apply();
  visibility();
  if (import.meta.env.DEV) {
    app.ticker.add(measure);
    Object.defineProperty(app, "framePacing", {
      configurable: true,
      get: () => {
        const sorted = [...samples].sort((a, b) => a - b);
        return {
          samples: sorted.length,
          p95: sorted[Math.floor(sorted.length * 0.95)] ?? 0,
          quality,
        };
      },
    });
  }
  const unsubscribe = subscribeGraphics(apply);
  document.addEventListener("visibilitychange", visibility);
  return () => {
    unsubscribe();
    document.removeEventListener("visibilitychange", visibility);
    if (import.meta.env.DEV) app.ticker.remove(measure);
  };
}
